import { FC, ReactElement, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { ILevelConfirmationPickerProps } from './levelConfirmationPicker.interface';
import styles from './levelConfirmationPicker.module.scss';

import { useLevelConfirmationTooltip } from 'hooks/useLevelConfirmationTooltip';
import { KnowledgeAreaLevelConfirmationApi } from 'services/knowledge-area-level-confirmation';
import { getAxiosErrorMessage } from 'utils/getAxiosErrorMessage';
import { Select } from 'views/components/UI/Form-elements/Select';
import { Headline4 } from 'views/components/UI/Headline';
import { CheckmarkRound } from 'views/components/UI/Icons/Icons';
import { useDialogBusy } from 'views/components/UI/Modal';
import { Text } from 'views/components/UI/Text';
import { Tooltip } from 'views/components/UI/Tooltip';
import { ISelectedLevel } from 'views/pages/KnowledgeArea/knowledge-area.interface';

const UNSET_LABEL_ID = 'knowledge-area-level-confirmation.label.unset';
const CLEAR_LABEL_ID = 'knowledge-area-level-confirmation.action.clear';
const ERROR_LABEL_ID = 'knowledge-area-level-confirmation.error.request-failed';
const LEVEL_CONFIRMATION_UPDATED_EVENT = 'level-confirmation-updated';

const sortByOrderNo = (levels: ISelectedLevel[]): ISelectedLevel[] =>
  [...levels].sort((a, b) => a.orderNo - b.orderNo);

const LevelConfirmationPicker: FC<ILevelConfirmationPickerProps> = ({
  accountId,
  knowledgeAreaId,
  levels,
  confirmation,
  readOnly,
  className,
  iconOnly,
  hideUnsetIcon,
  mode = 'secondary',
  onOpenChange,
}): ReactElement => {
  const intl = useIntl();
  const confirmationTooltip = useLevelConfirmationTooltip(confirmation);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const pendingRef = useRef(false);
  const isMountedRef = useRef(true);
  useDialogBusy(isSaving);

  useEffect(
    () => (): void => {
      isMountedRef.current = false;
    },
    [],
  );

  const sortedLevels = sortByOrderNo(levels);
  const confirmedLevel =
    confirmation?.confirmed && confirmation.confirmedLevelId !== null
      ? levels.find(level => level.id === confirmation.confirmedLevelId) ?? null
      : null;
  const confirmedLevelIds = new Set(confirmation?.confirmedLevelIds ?? []);

  const handleOpenChange = (open: boolean): void => {
    if (pendingRef.current) return;
    if (open) setErrorMessage('');
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const closePicker = (): void => {
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const onLevelClick = async (level: ISelectedLevel): Promise<void> => {
    if (pendingRef.current) return;
    if (confirmedLevel?.id === level.id) {
      closePicker();
      return;
    }

    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsSaving(true);
    setErrorMessage('');
    try {
      await new KnowledgeAreaLevelConfirmationApi().confirm({
        accountId,
        knowledgeAreaId,
        confirmedLevelId: level.id,
      });

      window.dispatchEvent(new Event(LEVEL_CONFIRMATION_UPDATED_EVENT));

      if (isMountedRef.current) {
        closePicker();
      }
    } catch (err) {
      if (isMountedRef.current) {
        setErrorMessage(
          getAxiosErrorMessage(err, intl.formatMessage({ id: ERROR_LABEL_ID })),
        );
      }
    } finally {
      pendingRef.current = false;
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const onClearClick = async (): Promise<void> => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsSaving(true);
    setErrorMessage('');
    try {
      await new KnowledgeAreaLevelConfirmationApi().clear(accountId, knowledgeAreaId);
      window.dispatchEvent(new Event(LEVEL_CONFIRMATION_UPDATED_EVENT));

      if (isMountedRef.current) {
        closePicker();
      }
    } catch (err) {
      if (isMountedRef.current) {
        setErrorMessage(
          getAxiosErrorMessage(err, intl.formatMessage({ id: ERROR_LABEL_ID })),
        );
      }
    } finally {
      pendingRef.current = false;
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const triggerLabel = confirmedLevel
    ? confirmedLevel.name
    : intl.formatMessage({ id: UNSET_LABEL_ID });
  const triggerColor = confirmedLevel
    ? 'var(--sk-viz-progress-confirmed)'
    : 'var(--sk-icon-subtle)';
  const showTriggerIcon = !hideUnsetIcon || Boolean(confirmedLevel);

  const tooltipLabel = confirmedLevel ? confirmationTooltip : triggerLabel;

  if (readOnly) {
    if (iconOnly) {
      return (
        <Tooltip content={tooltipLabel}>
          <CheckmarkRound
            size="16px"
            color={triggerColor}
            filled={Boolean(confirmedLevel)}
          />
        </Tooltip>
      );
    }

    const label = (
      <span className={styles.readOnly}>
        {showTriggerIcon && (
          <CheckmarkRound
            size="16px"
            color={triggerColor}
            filled={Boolean(confirmedLevel)}
          />
        )}
        <Text color="var(--sk-text-subtle)">{triggerLabel}</Text>
      </span>
    );
    return confirmedLevel ? <Tooltip content={tooltipLabel}>{label}</Tooltip> : label;
  }

  const options = sortedLevels.map((level, index) => ({
    value: String(level.id),
    label: level.name,
    disabled: isSaving,
    content: (
      <span className={styles.rowContent}>
        <span className={styles.rowIndex}>{index + 1}.</span>
        <Headline4 inline>{level.name}</Headline4>
        <span className={styles.rowSpacer} />
        {confirmedLevelIds.has(level.id) && (
          <CheckmarkRound size="16px" color="var(--sk-viz-progress-confirmed)" filled />
        )}
      </span>
    ),
  }));
  if (confirmation?.confirmed)
    options.push({
      value: 'clear',
      label: intl.formatMessage({ id: CLEAR_LABEL_ID }),
      disabled: isSaving,
      content: <span>{intl.formatMessage({ id: CLEAR_LABEL_ID })}</span>,
    });
  const renderConfirmationIcon = (): ReactElement => (
    <CheckmarkRound size="16px" color={triggerColor} filled={Boolean(confirmedLevel)} />
  );
  const select = (
    <Select
      appearance="compact"
      triggerMode={mode}
      className={confirmedLevel ? undefined : styles.unset}
      width="hug"
      iconOnly={iconOnly}
      aria-label={triggerLabel}
      displayValue={triggerLabel}
      value={confirmedLevel ? String(confirmedLevel.id) : ''}
      options={options}
      hasIcon={showTriggerIcon || iconOnly}
      getOptionIcon={renderConfirmationIcon}
      menuTitle={intl.formatMessage({
        id: 'knowledge-area-level-confirmation.picker.title',
      })}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      onChange={value => {
        if (pendingRef.current) return;
        if (value === 'clear') onClearClick();
        else {
          const level = levels.find(item => String(item.id) === value);
          if (level) onLevelClick(level);
        }
      }}
      error={Boolean(errorMessage)}
      errorMessage={errorMessage || undefined}
    />
  );
  return (
    <div className={classNames(styles.picker, className)}>
      <Tooltip content={tooltipLabel} interactiveChild hideBubble={isOpen}>
        {select}
      </Tooltip>
    </div>
  );
};

export { LevelConfirmationPicker };
